import React, { useState, useEffect } from 'react';
import { useCookies } from "react-cookie";
import axios from "axios";
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Chip,

  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import Layout from '../components/layout/GSTlayout';
import { format } from "date-fns";
import { 
  Pie,
  Bar 
} from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  ArcElement, 
  Tooltip, 
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';
import config from "../config.js";


ChartJS.register(
  ArcElement, 
  Tooltip, 
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const ActiveTasksPage = () => {
  const [cookies] = useCookies(["userId"]);
  const { API_BASE_URL } = config;
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    if (!cookies.userId) {
      showSnackbar("Missing user information", "error");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/getTasks?userId=${cookies.userId}`
      );
      
      if (response.data && response.data.status === "success" && Array.isArray(response.data.data)) {
        setTasks(response.data.data);
        if (response.data.data.length === 0) {
          showSnackbar("No tasks found", "info");
        }
      } else {
        setTasks([]);
        console.warn('Unexpected API response structure:', response.data);
        showSnackbar("Error loading tasks data", "error");
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      showSnackbar("Error fetching tasks", "error");
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  
  const statusChartData = {
    labels: ['Pending', 'In Progress', 'Completed'],
    datasets: [{
      data: [
        tasks.filter(task => task.status === 'Pending').length,
        tasks.filter(task => task.status === 'In Progress').length,
        tasks.filter(task => task.status === 'Completed').length
      ],
      backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56']
    }]
  };

  
  const priorityChartData = {
    labels: ['Low', 'Medium', 'High'],
    datasets: [{
      label: 'Task Priority Distribution',
      data: [
        tasks.filter(task => task.priority === 'Low').length,
        tasks.filter(task => task.priority === 'Medium').length,
        tasks.filter(task => task.priority === 'High').length
      ],
      backgroundColor: ['#4CAF50', '#FFA726', '#EF5350']
    }]
  };

  
  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

 
  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "In Progress":
        return "primary";
      case "Completed":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <Layout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Active Tasks Dashboard
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Tasks Overview Charts */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Task Status Distribution</Typography>
                  <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Pie data={statusChartData} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Task Priority Distribution</Typography>
                  <Box sx={{ height: 300 }}>
                    <Bar 
                      data={priorityChartData} 
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false }
                        }
                      }} 
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

         
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Active Tasks List
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Client Name</TableCell>
                          <TableCell>Task Type</TableCell>
                          <TableCell>Priority</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Due Date</TableCell>
                          <TableCell>Description</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tasks.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              No tasks found
                            </TableCell>
                          </TableRow>
                        ) : (
                          tasks.map((task) => (
                            <TableRow key={task._id}>
                              <TableCell>{task.clientName}</TableCell>
                              <TableCell>{task.taskType}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={task.priority} 
                                  color={getPriorityColor(task.priority)} 
                                  size="small" 
                                />
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={task.status} 
                                  color={getStatusColor(task.status)} 
                                  size="small" 
                                />
                              </TableCell>
                              <TableCell>
                                {format(new Date(task.dueDate), "dd MMM yyyy")}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 200 }}>
                                <Typography noWrap>{task.description}</Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default ActiveTasksPage;